import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Settings as SettingsIcon,
  DeleteSweep as DeleteSweepIcon,
} from '@mui/icons-material';
import { useAxios } from '../context/AxiosContext';

const ServerAdministration = () => {
  const { api } = useAxios();
  const [servers, setServers] = useState([]);
  const [serverChannels, setServerChannels] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingChannels, setLoadingChannels] = useState({});
  const [purgingChannels, setPurgingChannels] = useState({});
  const [alert, setAlert] = useState(null);

  // Fetch user's servers and their channels
  useEffect(() => {
    fetchServers();
  }, []);

  // Auto-fetch channels when servers are loaded
  useEffect(() => {
    servers.forEach(server => {
      if (!serverChannels[server.server_id]) {
        fetchChannelsForServer(server.server_id);
      }
    });
  }, [servers]);

  const fetchServers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/discord/servers/');
      setServers(response.data.servers || []);
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to load servers' });
      console.error('Error fetching servers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChannelsForServer = async (serverId) => {
    try {
      setLoadingChannels(prev => ({ ...prev, [serverId]: true }));
      const response = await api.get(`/discord/servers/${serverId}/channels/`);
      // Filter to only show text channels
      const textChannels = (response.data.channels || []).filter(channel => 
        channel.type === 0 || channel.type === 'GUILD_TEXT' || channel.type === 'text'
      );
      setServerChannels(prev => ({ ...prev, [serverId]: textChannels }));
    } catch (error) {
      setAlert({ type: 'error', message: `Failed to load channels for server ${serverId}` });
      console.error('Error fetching channels:', error);
    } finally {
      setLoadingChannels(prev => ({ ...prev, [serverId]: false }));
    }
  };

  const purgeChannelCache = async (serverId, channelId, channelName) => {
    try {
      setPurgingChannels(prev => ({ ...prev, [channelId]: true }));
      await api.post(`/discord/servers/${serverId}/channels/${channelId}/cache/purge/`);
      setAlert({ 
        type: 'success', 
        message: `Successfully purged and refreshed cache for #${channelName}` 
      });
    } catch (error) {
      setAlert({ 
        type: 'error', 
        message: `Failed to purge cache for #${channelName}: ${error.response?.data?.error || error.message}` 
      });
      console.error('Error purging cache:', error);
    } finally {
      setPurgingChannels(prev => ({ ...prev, [channelId]: false }));
    }
  };

  const purgeAllChannels = async (serverId, serverName) => {
    const channels = serverChannels[serverId] || [];
    if (channels.length === 0) return;

    try {
      setPurgingChannels(prev => {
        const newState = { ...prev };
        channels.forEach(channel => {
          newState[channel.id] = true;
        });
        return newState;
      });

      const purgePromises = channels.map(channel => 
        api.post(`/discord/servers/${serverId}/channels/${channel.id}/cache/purge/`)
      );

      await Promise.all(purgePromises);
      setAlert({ 
        type: 'success', 
        message: `Successfully purged cache for all ${channels.length} channels in ${serverName}` 
      });
    } catch (error) {
      setAlert({ 
        type: 'error', 
        message: `Failed to purge all channels: ${error.response?.data?.error || error.message}` 
      });
      console.error('Error purging all channels:', error);
    } finally {
      setPurgingChannels(prev => {
        const newState = { ...prev };
        channels.forEach(channel => {
          newState[channel.id] = false;
        });
        return newState;
      });
    }
  };



  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ 
          fontWeight: 700,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <SettingsIcon sx={{ fontSize: '2.5rem', color: '#6366f1' }} />
          Server Administration
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
          Manage Discord message caches for your registered servers and channels
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2, p: 2, backgroundColor: 'rgba(99, 102, 241, 0.05)', borderRadius: 2, borderLeft: '4px solid #6366f1' }}>
          <strong>What is cache purging?</strong> Purging clears the stored Discord messages from our cache and immediately re-fetches the latest messages from Discord. This is useful when messages appear out of sync, reactions are missing, or after Discord updates. Each purge operation fetches up to 100 recent messages per channel.
        </Typography>
      </Box>

      {alert && (
        <Alert 
          severity={alert.type} 
          onClose={() => setAlert(null)}
          sx={{ mb: 3 }}
        >
          {alert.message}
        </Alert>
      )}

      {servers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <StorageIcon sx={{ fontSize: '4rem', color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Discord Servers Registered
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You haven't registered any Discord servers yet. Register a server to manage its message cache.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => window.location.href = '/dashboard'}
            sx={{ 
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              '&:hover': { 
                background: 'linear-gradient(135deg, #5856eb 0%, #7c3aed 100%)' 
              }
            }}
          >
            Go to Dashboard
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {servers.map((server) => (
            <Grid item xs={12} key={server.server_id}>
              <Card sx={{ 
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                border: '1px solid rgba(99, 102, 241, 0.1)',
                '&:hover': { boxShadow: '0 8px 30px rgba(0,0,0,0.15)' }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                        {server.server_name || `Server ${server.server_id}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ID: {server.server_id}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Chip 
                          label={server.is_active ? 'Active' : 'Inactive'} 
                          color={server.is_active ? 'success' : 'default'}
                          size="small"
                        />
                        <Chip 
                          label={`Added ${new Date(server.created_date).toLocaleDateString()}`}
                          variant="outlined"
                          size="small"
                        />
                      </Stack>
                    </Box>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => purgeAllChannels(server.server_id, server.server_name || `Server ${server.server_id}`)}
                      disabled={loadingChannels[server.server_id] || !serverChannels[server.server_id] || serverChannels[server.server_id].length === 0}
                      startIcon={<DeleteSweepIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        '&:hover': { 
                          background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' 
                        }
                      }}
                    >
                      Purge All Channels
                    </Button>
                  </Box>

                  {server.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {server.description}
                    </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StorageIcon /> 
                    Text Channel Cache Management
                  </Typography>
                  
                  {loadingChannels[server.server_id] ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ ml: 2 }}>Loading channels...</Typography>
                    </Box>
                  ) : serverChannels[server.server_id] && (
                    <>
                      {serverChannels[server.server_id].length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                          No accessible text channels found for this server
                        </Typography>
                      ) : (
                        <List>
                          {serverChannels[server.server_id].map((channel) => (
                            <ListItem 
                              key={channel.id}
                              sx={{ 
                                border: '1px solid rgba(0,0,0,0.1)',
                                borderRadius: 2,
                                mb: 1,
                                '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.02)' }
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                      #{channel.name}
                                    </Typography>
                                    {channel.nsfw && (
                                      <Chip label="NSFW" color="warning" size="small" />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <Box>
                                    <Typography variant="body2" color="text.secondary">
                                      Channel ID: {channel.id}
                                    </Typography>
                                    {channel.topic && (
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Topic: {channel.topic}
                                      </Typography>
                                    )}
                                    {channel.parent && (
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Category: {channel.parent.name}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                              <ListItemSecondaryAction>
                                <Button
                                  variant="contained"
                                  color="error"
                                  size="small"
                                  onClick={() => purgeChannelCache(server.server_id, channel.id, channel.name)}
                                  disabled={purgingChannels[channel.id]}
                                  startIcon={purgingChannels[channel.id] ? <CircularProgress size={16} /> : <DeleteSweepIcon />}
                                  sx={{
                                    minWidth: '140px',
                                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                    '&:hover': { 
                                      background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' 
                                    }
                                  }}
                                >
                                  {purgingChannels[channel.id] ? 'Purging...' : 'Purge Cache'}
                                </Button>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}


    </Container>
  );
};

export default ServerAdministration;
